import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    getUsers,
    getWorkspaces,
    getWorkspacesForUser,
    addUserToWorkspace,
    removeUserFromWorkspace,
    createWorkspace,
    getWorkspaceInboxes,
    clearAICache,
} from './scoopAdminApi'

export const useScoopAdmin = (
    userSearch,
    workspaceSearch,
    targetUserID,
    workspaceSearchBy = 'name',
    selectedWorkspaceIDForCache
) => {
    const queryClient = useQueryClient()

    // ==========================================================================
    // Queries
    // ==========================================================================

    const {
        data: users,
        isLoading: loadingUsers,
        isFetching: fetchingUsers,
    } = useQuery({
        queryKey: ['adminUsers', userSearch],
        queryFn: () => getUsers(userSearch),
        enabled: userSearch.length >= 3,
    })

    const {
        data: workspaces,
        isLoading: loadingWorkspaces,
        isFetching: fetchingWorkspaces,
    } = useQuery({
        queryKey: ['adminWorkspaces', workspaceSearch, workspaceSearchBy],
        queryFn: () => getWorkspaces(workspaceSearch, workspaceSearchBy),
        enabled: workspaceSearch.length >= 2,
    })

    const { data: userWorkspaces, isLoading: loadingUserWorkspaces } = useQuery({
        queryKey: ['adminUserWorkspaces', targetUserID],
        queryFn: () => getWorkspacesForUser(targetUserID),
        enabled: Boolean(targetUserID),
    })

    const { data: workspaceInboxes, isLoading: loadingInboxes } = useQuery({
        queryKey: ['adminWorkspaceInboxes', selectedWorkspaceIDForCache],
        queryFn: () => getWorkspaceInboxes(selectedWorkspaceIDForCache),
        enabled: Boolean(selectedWorkspaceIDForCache),
    })

    // ==========================================================================
    // Mutations
    // ==========================================================================

    const { mutate: addUser, isPending: isAddingUser } = useMutation({
        mutationFn: addUserToWorkspace,
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['adminUserWorkspaces', variables.userID] })
            if (variables.onSuccess) variables.onSuccess(response)
        },
        onError: (error, variables) => {
            if (variables.onError) variables.onError(error)
        },
    })

    const { mutate: removeUser, isPending: isRemovingUser } = useMutation({
        mutationFn: removeUserFromWorkspace,
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['adminUserWorkspaces', variables.userID] })
            if (variables.onSuccess) variables.onSuccess(response)
        },
        onError: (error, variables) => {
            if (variables.onError) variables.onError(error)
        },
    })

    const { mutate: createNewWorkspace, isPending: isCreatingWorkspace } = useMutation({
        mutationFn: createWorkspace,
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: ['adminWorkspaces'] })
            if (variables.onSuccess) variables.onSuccess(response)
        },
        onError: (error, variables) => {
            if (variables.onError) variables.onError(error)
        },
    })

    const { mutate: clearCache, isPending: isClearingCache } = useMutation({
        mutationFn: clearAICache,
        onSuccess: (response, variables) => {
            if (variables.onSuccess) variables.onSuccess(response)
        },
        onError: (error, variables) => {
            if (variables.onError) variables.onError(error)
        },
    })

    // ==========================================================================
    // Return
    // ==========================================================================

    return {
        // Users search
        users: users || [],
        loadingUsers: loadingUsers || fetchingUsers,

        // Workspaces search
        workspaces: workspaces || [],
        loadingWorkspaces: loadingWorkspaces || fetchingWorkspaces,

        // User's workspaces
        userWorkspaces: userWorkspaces || [],
        loadingUserWorkspaces,

        // Mutations
        addUser,
        isAddingUser,
        removeUser,
        isRemovingUser,
        createNewWorkspace,
        isCreatingWorkspace,
        clearCache,
        isClearingCache,

        // Workspace inboxes for cache clearing
        workspaceInboxes: workspaceInboxes?.inboxes || [],
        loadingInboxes,
    }
}
